import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, imageUrl } from "../lib/api";
import { Loader2, ImageOff, X, ChevronLeft, ChevronRight, Tractor, IndianRupee } from "lucide-react";

const formatMrp = (v) => {
  if (v == null) return null;
  try {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(v);
  } catch {
    return String(v);
  }
};

// Fullscreen lightbox gallery for a single part: header metadata + main image + prev/next + thumbnails + keyboard nav.
const PartDetailDialog = ({ result, open, onClose }) => {
  const partNo = result?.part_no;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [imgErr, setImgErr] = useState({});

  useEffect(() => {
    if (!open || !partNo) return;
    setLoading(true);
    setData(null);
    setIdx(0);
    setImgErr({});
    axios
      .get(`${API}/parts/${encodeURIComponent(partNo)}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [open, partNo]);

  // Prefer stored/viewable images; fall back to the result's best image if metadata still loading.
  const imageIds =
    (data?.viewable_image_ids && data.viewable_image_ids.length
      ? data.viewable_image_ids
      : result?.image_ids && result.image_ids.length
      ? result.image_ids
      : result?.best_image_id
      ? [result.best_image_id]
      : []) || [];
  const total = imageIds.length;

  const go = useCallback(
    (delta) => {
      if (total === 0) return;
      setIdx((p) => (p + delta + total) % total);
    },
    [total]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go, onClose]);

  if (!open) return null;

  const description = data?.description ?? result?.description;
  const models = data?.models ?? result?.models ?? [];
  const mrp = data?.mrp ?? result?.mrp;
  const currentId = imageIds[idx];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
      data-testid="part-detail-dialog"
      onClick={onClose}
    >
      {/* Header */}
      <div
        className="flex items-start justify-between gap-4 px-4 sm:px-8 py-4 border-b border-white/10 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0">
          <div className="font-mono text-lg sm:text-xl font-bold truncate" data-testid="detail-part-no">
            {partNo}
          </div>
          {description ? (
            <div className="text-sm text-white/70 mt-0.5 line-clamp-2 max-w-2xl">{description}</div>
          ) : (
            <div className="text-xs text-white/40 mt-0.5 italic">No description available</div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {models.length > 0 ? (
              models.map((m) => (
                <span
                  key={m}
                  className="flex items-center gap-1 text-[11px] bg-white/10 text-white/90 px-2 py-0.5 rounded-sm"
                >
                  <Tractor className="w-3 h-3" /> {m}
                </span>
              ))
            ) : (
              <span className="text-[11px] text-white/40">Models: not available</span>
            )}
            {mrp != null && (
              <span className="flex items-center gap-0.5 text-[11px] bg-brand text-white px-2 py-0.5 rounded-sm font-semibold">
                <IndianRupee className="w-3 h-3" /> {formatMrp(mrp)}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="lightbox-close"
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white/80 hover:bg-white/10 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main viewer */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="flex items-center gap-2 text-white/70">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading images…
          </div>
        ) : total === 0 ? (
          <div className="flex flex-col items-center gap-3 text-white/50">
            <ImageOff className="w-10 h-10" />
            <span className="text-sm">No viewable images yet for this part.</span>
          </div>
        ) : (
          <>
            {total > 1 && (
              <button
                onClick={() => go(-1)}
                data-testid="lightbox-prev"
                className="absolute left-2 sm:left-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <div className="w-full h-full flex items-center justify-center p-4 sm:p-10">
              {imgErr[currentId] ? (
                <div className="flex flex-col items-center gap-2 text-white/50">
                  <ImageOff className="w-10 h-10" />
                  <span className="text-xs">Image unavailable</span>
                </div>
              ) : (
                <img
                  key={currentId}
                  src={imageUrl(currentId)}
                  alt={`${partNo} ${idx + 1}`}
                  data-testid="lightbox-image"
                  onError={() => setImgErr((e) => ({ ...e, [currentId]: true }))}
                  className="max-h-full max-w-full object-contain select-none"
                />
              )}
            </div>
            {total > 1 && (
              <button
                onClick={() => go(1)}
                data-testid="lightbox-next"
                className="absolute right-2 sm:right-6 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                aria-label="Next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
            <div className="absolute top-3 right-4 text-white/60 text-xs font-mono">
              {idx + 1} / {total}
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {total > 1 && (
        <div
          className="flex gap-2 overflow-x-auto px-4 sm:px-8 py-4 border-t border-white/10"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-thumbs"
        >
          {imageIds.map((id, i) => (
            <button
              key={id}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 bg-white flex items-center justify-center transition-colors ${
                i === idx ? "border-brand" : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              {imgErr[id] ? (
                <ImageOff className="w-4 h-4 text-muted-foreground" />
              ) : (
                <img src={imageUrl(id)} alt="thumb" className="max-h-full max-w-full object-contain" loading="lazy" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartDetailDialog;
